/**
 * RFC 5545 content-line primitives.
 *
 * Two rules that are easy to get wrong and break real calendar imports:
 * lines are folded at 75 OCTETS (not characters), and every line ends CRLF.
 */

const MAX_OCTETS = 75;

const encoder = new TextEncoder();

/** Escapes a TEXT value: backslash, semicolon, comma, and newlines. */
export function escapeText(value: string): string {
  return value
    .replace(/\\/gu, '\\\\')
    .replace(/;/gu, '\\;')
    .replace(/,/gu, '\\,')
    .replace(/\r\n|\r|\n/gu, '\\n');
}

/**
 * Folds one content line to 75 octets, continuing with a leading space.
 *
 * Folding counts UTF-8 octets and never splits a code point, so multi-byte
 * characters in a class name survive the round trip.
 */
export function foldLine(line: string): string {
  if (encoder.encode(line).length <= MAX_OCTETS) {
    return line;
  }

  const pieces: string[] = [];
  let current = '';
  let currentOctets = 0;
  // The continuation prefix costs one octet, so subsequent lines carry less.
  let budget = MAX_OCTETS;

  for (const character of line) {
    const size = encoder.encode(character).length;
    if (currentOctets + size > budget) {
      pieces.push(current);
      current = '';
      currentOctets = 0;
      budget = MAX_OCTETS - 1;
    }
    current += character;
    currentOctets += size;
  }
  pieces.push(current);

  return pieces.join('\r\n ');
}

/** Joins content lines into a folded, CRLF-terminated document. */
export function serializeLines(lines: readonly string[]): string {
  return `${lines.map(foldLine).join('\r\n')}\r\n`;
}
